import { useState, useEffect } from "react";
import { NavLink, Link } from "react-router-dom";
import {
  LayoutDashboard,
  School,
  Users,
  BarChart2,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  Moon,
  Sun,
  Building2,
  WifiOff,
  Settings
} from "lucide-react";
import { UserButton, useUser } from "@clerk/clerk-react";
import useSubscriptionInfo from "@/hooks/useSubscriptionInfo";

const navItems = [
  { name: "Dashboard", path: "/dashboard", icon: LayoutDashboard },
  { name: "My Schools", path: "/schools", icon: Building2 },
  { name: "My Classes", path: "/classes", icon: School },
  { name: "Shared Classes", path: "/shared-classes", icon: Users }  
];

const planBadgeStyle = {
  free: { bg: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)" },
  standard: { bg: "rgba(120,193,138,0.25)", color: "#a3d9b1" },
  professional: { bg: "rgba(96,165,250,0.2)", color: "#93c5fd" },
};

export default function Sidebar({ collapsed, onToggle }) {
  const { user } = useUser();
  const { planName, loading } = useSubscriptionInfo();
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [theme, setTheme] = useState(localStorage.getItem("theme") || "light");
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {                                                                               
    const goOnline  = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);                                                   
    window.addEventListener("online",  goOnline);                                                 
    window.addEventListener("offline", goOffline);                                                
                                                                                                  
    const check = async () => {                                                                   
      try {
        await fetch(`${import.meta.env.VITE_BACKEND_URL || "http://localhost:8080/"}api/health`, {
          method: "HEAD", cache: "no-store", signal: AbortSignal.timeout(3000),                   
        });                                                                                       
        setIsOnline(true);                                                                        
      } catch {                                                                                   
        setIsOnline(false);
      }
    };
                                                                                                  
    const interval = setInterval(check, 10000);
                                                                                                  
    return () => {
      window.removeEventListener("online",  goOnline);
      window.removeEventListener("offline", goOffline);
      clearInterval(interval);                                                                    
    };
  }, []);  
    useEffect(() => {
    if (!showSettings) return;
    const close = (e) => {
      if (!e.target.closest(".sidebar-footer")) setShowSettings(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [showSettings]);
  


  const handleThemeToggle = () => {
    const next = theme === "light" ? "dark" : "light";
    localStorage.setItem("theme", next);
    setTheme(next);
  };

  const badge = planBadgeStyle[planName] || planBadgeStyle.free;
  const planLabel = planName
    ? planName.charAt(0).toUpperCase() + planName.slice(1)
    : "Free";

  return (
    <aside className={`app-sidebar${collapsed ? " collapsed" : ""}`} style={ showSettings ? {overflow: "visible"} : {overflow: "hidden"} } >
      {/* Header */}
      <div className="sidebar-header">
        {!collapsed && (
          <Link to="/dashboard" className="sidebar-brand">
            <img src="/images/easyreports_logo.png" alt="EasyReports logo" style={{ height: 20, width: 20, objectFit: "contain", flexShrink: 0 }} />
            <span>EasyReports</span>
          </Link>
        )}
        <button
          className="sidebar-collapse-btn"
          onClick={onToggle}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          style={collapsed ? { margin: "0 auto" } : {}}
        >
          {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {!collapsed && <span className="sidebar-nav-label">Navigation</span>}
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === "/dashboard"}
            className={({ isActive }) =>
              `sidebar-nav-item${isActive ? " active" : ""}`
            }
            title={collapsed ? item.name : undefined}
          >
            <item.icon size={18} className="sidebar-nav-icon" />
            {!collapsed && <span>{item.name}</span>}
          </NavLink>
        ))}
      </nav>
      
      {/*Offline Warning */}
      { !isOnline && (
        <div
        className = "mx-3 mb-3 rounded-lg px-3 py-2.5 flex items-center gap-2 text-xs font-medium"
        style={{ background:"rgba(239, 68, 68, 0.15)", color: "#fca5a5", border: "1px solid rgba(239, 68, 68, 0.25)"  }} >
        <WifiOff size={13} style={{flexShrink: 0 }} />
        {!collapsed && <span>Check Network Status: <br/>your changes may not be saved</span>}
        </div>
      )}

{/* Footer */}
  <div className="sidebar-footer">
    {!collapsed && !loading && (
      <div className="sidebar-user-info">
        <span className="sidebar-user-name">
          {user?.firstName || user?.fullName || "Teacher"}
        </span>
        <span
          className="sidebar-plan-badge"
          style={{ background: badge.bg, color: badge.color }}
        >
          {planLabel} Plan
        </span>
      </div>
    )}

    {/* Settings popover */}
    {showSettings && (
      <div
        style={{
          position: "absolute",
          bottom: "48px",
          left: collapsed ? "72px" : "12px",
          right: collapsed ? "auto" : "12px",
          background: "var(--sidebar-bg, #1a2e25)",
          border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: "12px",
          padding: "8px",
          zIndex: 50,
          minWidth: "160px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        }}
      >
        <Link
          to="/usage"
          onClick={() => setShowSettings(false)}
          style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "8px", color:
  "rgba(255,255,255,0.75)", fontSize: "13px", textDecoration: "none" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <BarChart2 size={14} /> Usage
        </Link>
        <Link
          to="/billing"
          onClick={() => setShowSettings(false)}
          style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "8px", color:
  "rgba(255,255,255,0.75)", fontSize: "13px", textDecoration: "none" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          <CreditCard size={14} /> Billing
        </Link>
        <div style={{ height: "1px", background: "rgba(255,255,255,0.08)", margin: "6px 4px" }} />
        <button
          onClick={handleThemeToggle}
          style={{ display: "flex", alignItems: "center", gap: "8px", padding: "8px 10px", borderRadius: "8px", color: 
  "rgba(255,255,255,0.75)", fontSize: "13px", background: "transparent", border: "none", width: "100%", cursor: "pointer" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.08)"}
          onMouseLeave={e => e.currentTarget.style.background = "transparent"}
        >
          {theme === "light" ? <Moon size={14} /> : <Sun size={14} />}
          {theme === "light" ? "Dark mode" : "Light mode"}
        </button>
      </div>
    )}

    <div
      className="sidebar-footer-actions"
      style={{ flexDirection: collapsed ? "column" : "row" }}
    >
      <button
        className="sidebar-icon-btn"
        onClick={() => setShowSettings(s => !s)}
        title="Settings"
      >
        <Settings size={15} />
      </button>
      <UserButton afterSignOutUrl="/" />
    </div>
  </div>


    </aside>
  );
}
